import React, { Component, ReactElement } from "react";
import PropTypes from "prop-types";

import GTMTracker from "../../utils/tracking/GTMTracker";
import classnames from "classnames";
import UniqueId from "../../utils/UniqueId";
import { LEFT, RIGHT } from "../../constants/Keys";

interface State {
    selectedIndex: number;
}

type Props = PropTypes.InferPropsInner<typeof Tabs.propTypes>;

export default class Tabs extends Component<Props, State> {
    _componentId: string;

    _tabEls: any[];

    constructor(props) {
        super(props);
        this.state = { selectedIndex: this.props.selectedIndex ?? 0 };
        this._componentId = UniqueId.generate("tabs");
        this._tabEls = [];
    }

    render() {
        return (
            <div className="tabs">
                {this._renderLabels()}
                {this._renderPanes()}
            </div>
        );
    }

    componentDidUpdate(prevProps) {
        if (
            prevProps.selectedIndex !== this.props.selectedIndex &&
            typeof this.props.selectedIndex === "number"
        ) {
            this.setState({ selectedIndex: this.props.selectedIndex });
        }
    }

    _renderLabels() {
        const labelEls = React.Children.toArray(this.props.children).map(
            (pane: ReactElement, index) => {
                const paneId = pane.props.paneId;
                const isSelected = index === this.state.selectedIndex;
                return (
                    <button
                        key={`label-${index}`}
                        role="tab"
                        aria-selected={isSelected}
                        aria-controls={this._getPaneHtmlId(paneId)}
                        className={classnames("tabs__label", paneId, {
                            "tabs__label--selected": isSelected,
                        })}
                        id={this._getTabHtmlId(index)}
                        onClick={() => this._changeTab(index, paneId)}
                        tabIndex={isSelected ? 0 : -1}
                        onKeyDown={this._handleKeyDown}
                        ref={(el) => (this._tabEls[index] = el)}
                    >
                        {pane.props.label}
                    </button>
                );
            }
        );

        return (
            <div role="tablist" className="tabs__labels-container">
                {labelEls}
            </div>
        );
    }

    _changeTab(index, paneId) {
        if (this.state.selectedIndex === index) {
            return;
        }

        this._tabEls[index].focus();
        this.setState({ selectedIndex: index });

        GTMTracker.trackEngagement({
            bvpEventAction: "click",
            bvpEventLabel: `${this.props.eventPrefix}.${paneId}`,
        });

        if (this.props.onSelectedIndexChange) {
            this.props.onSelectedIndexChange(index);
        }
    }

    _renderPanes() {
        const paneEls = React.Children.toArray(this.props.children).map(
            (pane: ReactElement, index) => {
                const isSelected = index === this.state.selectedIndex;
                return (
                    <div
                        id={this._getPaneHtmlId(pane.props.paneId)}
                        key={`pane-${index}`}
                        aria-labelledby={this._getTabHtmlId(index)}
                        className={classnames("tabs__pane", {
                            "tabs__pane--selected": isSelected,
                        })}
                        role="tabpanel"
                        tabIndex={0}
                    >
                        {pane}
                    </div>
                );
            }
        );

        return <div className="tabs__panes-container">{paneEls}</div>;
    }

    _handleKeyDown = (event) => {
        switch (event.key) {
            case LEFT:
                this._moveSelectedTab(-1);
                event.preventDefault();
                break;
            case RIGHT:
                this._moveSelectedTab(1);
                event.preventDefault();
                break;
        }
    };

    _moveSelectedTab(step) {
        const childrenCount = React.Children.count(this.props.children);
        const newSelectedIndex =
            (this.state.selectedIndex + step + childrenCount) % childrenCount;
        const paneId = (
            React.Children.toArray(this.props.children)[
                newSelectedIndex
            ] as ReactElement
        ).props.paneId;
        this._changeTab(newSelectedIndex, paneId);
    }

    _getPaneHtmlId(paneId) {
        return `${this._componentId}-${paneId}`;
    }

    _getTabHtmlId(tabIndex) {
        return `${this._componentId}-${tabIndex}`;
    }

    static defaultProps = {
        selectedIndex: 0,
        onSelectedIndexChange: () => {},
        eventPrefix: "",
    };

    static propTypes = {
        selectedIndex: PropTypes.number,
        onSelectedIndexChange: PropTypes.func,
        eventPrefix: PropTypes.string,
        children: PropTypes.any,
    };
}
